import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/enhanced/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/enhanced/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// Define a parser function to handle number conversion
const parseNumber = (value: string): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

const bookingFormSchema = z.object({
  companyName: z.string().min(1, { message: "Bedriftsnavn er påkrevd" }),
  contactName: z.string().min(1, { message: "Kontaktperson er påkrevd" }),
  contactEmail: z.string().email({ message: "Gyldig e-post er påkrevd" }),
  contactPhone: z.string().min(8, { message: "Gyldig telefonnummer er påkrevd" }),
  dateRange: z.object({
    from: z.date(),
    to: z.date()
  }).refine(data => data.from && data.to, {
    message: "Både start- og sluttdato er påkrevd",
    path: ["dateRange"]
  }),
  price: z.preprocess(
    parseNumber,
    z.number().min(1, { message: "Pris er påkrevd" })
  ),
  placement: z.enum(["top", "normal"]),
  status: z.enum(["planlagt", "aktiv", "fullført", "kansellert"]),
  notes: z.string().optional(),
  articleTitle: z.string().optional()
});

type FormValues = z.infer<typeof bookingFormSchema>;

const BookingForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      companyName: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      dateRange: { from: undefined, to: undefined } as any,
      price: undefined,
      placement: "normal",
      status: "planlagt",
      notes: "",
      articleTitle: ""
    }
  });

  const [tabValue, setTabValue] = React.useState('info');
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);
  const dateError = watch('dateRange.from') && watch('dateRange.to') && watch('dateRange.from') > watch('dateRange.to');

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
  };

  const onSubmit = (data: FormValues) => {
    console.log("Form data:", data);
    toast.success("Booking lagret!");
  };

  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>Ny booking</DialogTitle>
      </DialogHeader>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Bedriftsinformasjon</TabsTrigger>
          <TabsTrigger value="details">Bookingdetaljer</TabsTrigger>
        </TabsList>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
          <TabsContent value="info">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="companyName">Bedriftsnavn</Label>
                <Input
                  id="companyName"
                  placeholder="Bedriftsnavn"
                  {...register('companyName')}
                  className={errors.companyName ? 'border-red-500' : ''}
                />
                {errors.companyName && (
                  <p className="text-red-500 text-sm">{errors.companyName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">Kontaktperson</Label>
                <Input
                  id="contactName"
                  placeholder="Navn på kontaktperson"
                  {...register('contactName')}
                  className={errors.contactName ? 'border-red-500' : ''}
                />
                {errors.contactName && (
                  <p className="text-red-500 text-sm">{errors.contactName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">E-postadresse</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="E-post til kontaktperson"
                  {...register('contactEmail')}
                  className={errors.contactEmail ? 'border-red-500' : ''}
                />
                {errors.contactEmail && (
                  <p className="text-red-500 text-sm">{errors.contactEmail.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Telefonnummer</Label>
                <Input
                  id="contactPhone"
                  placeholder="Telefonnummer"
                  {...register('contactPhone')}
                  className={errors.contactPhone ? 'border-red-500' : ''}
                />
                {errors.contactPhone && (
                  <p className="text-red-500 text-sm">{errors.contactPhone.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                onClick={() => setTabValue('details')}
                className="bg-[#ea384c] text-white"
              >
                Neste
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="datePicker">Periode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !startDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, 'dd.MM.yyyy') : <span>Fra dato</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={handleStartDateChange}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !endDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, 'dd.MM.yyyy') : <span>Til dato</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={handleEndDateChange}
                          disabled={(date) =>
                            startDate ? date < startDate : false
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {dateError && <p className="text-red-500 text-sm">{dateError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Pris (NOK)</Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="0"
                  {...register('price', {
                    valueAsNumber: true,
                  })}
                  className={errors.price ? 'border-red-500' : ''}
                />
                {errors.price && (
                  <p className="text-red-500 text-sm">{errors.price.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  defaultValue="planlagt"
                  onValueChange={(value) => setValue('status', value as any)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planlagt">Planlagt</SelectItem>
                    <SelectItem value="aktiv">Aktiv</SelectItem>
                    <SelectItem value="fullført">Fullført</SelectItem>
                    <SelectItem value="kansellert">Kansellert</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="placement">Plassering</Label>
                <Select
                  defaultValue="normal"
                  onValueChange={(value) => setValue('placement', value as any)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg plassering" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Standard</SelectItem>
                    <SelectItem value="top">Topp 10</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <Label htmlFor="notes">Notater</Label>
                <Textarea
                  id="notes"
                  placeholder="Legg til notater om bookingen her..."
                  {...register('notes')}
                  rows={4}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <Button type="button" variant="outline" onClick={() => setTabValue('info')}>
                Tilbake
              </Button>
              <Button type="submit" className="bg-[#ea384c] text-white" disabled={isSubmitting}>
                {isSubmitting ? 'Lagrer...' : 'Lagre booking'}
              </Button>
            </div>
          </TabsContent>
        </form>
      </Tabs>
    </DialogContent>
  );
};

export default BookingForm;
